/**
 * ResultHighlight — render a string with matched character ranges marked.
 *
 * Takes the result.title and the rank()-returned positions[]. Positions
 * point into the ORIGINAL (non-normalized) string indices, so we can wrap
 * runs of matched chars in <mark> directly.
 *
 * Visual: matched runs get a soft primary-tinted background and slightly
 * bolder weight. Subtle — Stripe/Vercel-style, not highlighter-yellow.
 */
"use client";

import { Fragment } from "react";
import { cn } from "@/shared/lib/utils";

interface ResultHighlightProps {
  text: string;
  positions?: number[];
  className?: string;
}

export function ResultHighlight({ text, positions, className }: ResultHighlightProps) {
  if (!positions || positions.length === 0) {
    return <span className={className}>{text}</span>;
  }
  // Group adjacent positions into runs so we render fewer <mark>s.
  const runs: Array<{ start: number; end: number }> = [];
  let runStart = positions[0];
  let prev = positions[0];
  for (let i = 1; i < positions.length; i++) {
    const p = positions[i];
    if (p === prev + 1) {
      prev = p;
      continue;
    }
    runs.push({ start: runStart, end: prev + 1 });
    runStart = p;
    prev = p;
  }
  runs.push({ start: runStart, end: prev + 1 });

  // Walk the text, splitting on each run.
  const parts: Array<{ kind: "plain" | "mark"; text: string; key: number }> = [];
  let cursor = 0;
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i];
    if (cursor < r.start) {
      parts.push({ kind: "plain", text: text.slice(cursor, r.start), key: parts.length });
    }
    parts.push({ kind: "mark", text: text.slice(r.start, r.end), key: parts.length });
    cursor = r.end;
  }
  if (cursor < text.length) {
    parts.push({ kind: "plain", text: text.slice(cursor), key: parts.length });
  }

  return (
    <span className={className}>
      {parts.map((p) =>
        p.kind === "mark" ? (
          <mark
            key={p.key}
            className={cn(
              "rounded-[3px] bg-primary/15 px-[1px] font-semibold text-foreground",
              "decoration-clone",
            )}
          >
            {p.text}
          </mark>
        ) : (
          <Fragment key={p.key}>{p.text}</Fragment>
        ),
      )}
    </span>
  );
}
